import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Members whose username starts with what's typed in Browse search, shown as
 * a small dropdown under the box, each labelled "User" (owner request
 * 2026-10-12, after Reverb's search). Pedal filtering is untouched: this sits
 * beside it, and picking a member opens their public profile.
 *
 * Shown under Browse's search boxes and the web top-nav search.
 *
 * Prefix match on publicProfiles.usernameLowercase (public read, a single-
 * field range so no composite index). Accounts from before that field existed
 * are not found; banned accounts are dropped.
 */
export const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 5;
const results = new Map(); // normalized query -> Promise<member[]>

export function normalizeUserQuery(text) {
  return typeof text === 'string' ? text.trim().replace(/^@/, '').toLowerCase() : '';
}

export function searchMembers(text) {
  const q = normalizeUserQuery(text);
  if (q.length < MIN_QUERY_LENGTH) return Promise.resolve([]);
  if (!results.has(q)) {
    const pending = getDocs(query(
      collection(db, 'publicProfiles'),
      where('usernameLowercase', '>=', q),
      where('usernameLowercase', '<=', `${q}`),
      orderBy('usernameLowercase'),
      limit(MAX_RESULTS + 3),
    ))
      .then((snap) => snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => p.username && !p.isBanned)
        .slice(0, MAX_RESULTS))
      .catch(() => {
        results.delete(q); // let a later keystroke retry
        return [];
      });
    results.set(q, pending);
  }
  return results.get(q);
}

// True when the box is really on screen. Browse renders the same search row
// more than once (in-flow, fixed-on-scroll, desktop overlay) and hides the
// unused copies with display:none, visibility:hidden or opacity:0; a portal
// would escape all three, so the copy that owns the dropdown is checked here.
function isOnScreen(el) {
  if (!el || el.getClientRects().length === 0) return false;
  for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
    const cs = window.getComputedStyle(node);
    if (cs.display === 'none' || cs.visibility === 'hidden' || (cs.opacity !== '' && Number(cs.opacity) === 0)) return false;
  }
  return true;
}

const DEBOUNCE_MS = 250;

/**
 * Drawn in a portal at the search box's position (position:fixed), because
 * the mobile Browse header clips its search row (overflow:hidden) and cut
 * the dropdown off. `searchText` may be raw input; it is debounced here.
 */
const UserSearchSuggestions = ({ searchText, onSelect }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [rect, setRect] = useState(null);
  const anchorRef = useRef(null);
  const boxRef = useRef(null);
  // Browse restores its last search when you come back to it; suggestions
  // wait until the text is edited so they don't reopen on their own.
  const mountTextRef = useRef(searchText);
  const typed = searchText !== mountTextRef.current;

  useEffect(() => {
    let current = true;
    setDismissed(false);
    const timer = setTimeout(() => {
      searchMembers(searchText).then((found) => { if (current) setMembers(found); });
    }, DEBOUNCE_MS);
    return () => { current = false; clearTimeout(timer); };
  }, [searchText]);

  const open = typed && members.length > 0 && !dismissed && normalizeUserQuery(searchText).length >= MIN_QUERY_LENGTH;

  // Follow the search box while open (the Browse header moves on scroll).
  useEffect(() => {
    if (!open) { setRect(null); return undefined; }
    const measure = () => {
      const field = anchorRef.current && anchorRef.current.parentElement;
      if (!isOnScreen(field)) { setRect(null); return; }
      const r = field.getBoundingClientRect();
      // Open upward when the box sits low on the screen (the nav's mobile
      // menu search is pinned to the bottom).
      const below = window.innerHeight - r.bottom;
      const up = below < 220 && r.top > below;
      setRect(up
        ? { bottom: window.innerHeight - r.top + 6, left: r.left, width: r.width, room: r.top - 12 }
        : { top: r.bottom + 6, left: r.left, width: r.width, room: below - 12 });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, members]);

  // Tapping anywhere else (the pedal grid, filters) closes it until the
  // search text changes again.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target) && !(e.target.closest && e.target.closest('input'))) {
        setDismissed(true);
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const choose = (m) => {
    setDismissed(true);
    if (onSelect) onSelect(m); // e.g. close the nav's mobile menu
    navigate(`/profile/${m.id}`);
  };

  return (
    <>
      <span ref={anchorRef} aria-hidden="true" style={{ display: 'none' }} />
      {open && rect && createPortal(
        <div
          ref={boxRef}
          role="listbox"
          aria-label="Matching members"
          style={{
            position: 'fixed', top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width, zIndex: 1000,
            maxHeight: Math.max(120, Math.round(rect.room)), overflowY: 'auto',
            backgroundColor: 'white', borderRadius: '14px', border: '1px solid #e5e7eb',
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.14)', boxSizing: 'border-box',
            fontFamily: 'Nunito, sans-serif', textAlign: 'left',
          }}
        >
          {members.map((m, i) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected="false"
              // Keep focus in the search box so tapping a member isn't eaten by
              // the keyboard closing first.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(m)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', width: '100%', boxSizing: 'border-box',
                padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
                borderTop: i === 0 ? 'none' : '1px solid #f3f4f6', fontFamily: 'inherit', textAlign: 'left',
              }}
            >
              {m.photoURL ? (
                <img src={m.photoURL} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <span style={{
                  width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0, backgroundColor: 'rgba(201, 162, 39, 0.16)',
                  color: '#8a6d12', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px',
                }}>
                  {m.username.charAt(0).toUpperCase()}
                </span>
              )}
              <span style={{ flex: 1, minWidth: 0, fontSize: '15px', fontWeight: 700, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.username}
              </span>
              <span style={{
                flexShrink: 0, fontSize: '11px', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
                color: '#8a6d12', backgroundColor: 'rgba(201, 162, 39, 0.16)', borderRadius: '9999px', padding: '3px 9px',
              }}>
                User
              </span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default UserSearchSuggestions;
